/**
 * Plan 10, Task 1.2 — PerfStats collector with __perfStats window hook
 * and 1-second sliding-window FPS counter.
 *
 * The collector reads from a WebGLRenderer's `.info` block each tick and
 * publishes its current snapshot to `globalThis.__perfStats` (or an injected
 * `globalTarget` for testing) so external probes (Task 6.1 puppeteer fixture,
 * docs/policies/perf-gates.md §Enforcement) can read it without importing
 * this module.
 *
 * TIME SOURCE: an optional `now()` thunk is accepted for testability
 * (vi.useFakeTimers + stub `now()`). Default: `() => performance.now()`.
 */

import * as THREE from 'three';
import type { PerfStats } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/** Increment frame, refresh sliding window, publish snapshot, return it. */
export interface PerfStatsCollector {
  /** Increment frame, refresh sliding window, publish snapshot. */
  tick(): PerfStats;
  /** Read current snapshot without ticking. */
  snapshot(): PerfStats;
  /** Reset to initial state (frame=0, elapsedMs=0, empty FPS window). */
  reset(): void;
  /** Idempotent — clears the global hook. */
  dispose(): void;
}

export interface PerfStatsConfig {
  readonly renderer: THREE.WebGLRenderer;
  /** Optional time source for testing. Default: `() => performance.now()`. */
  readonly now?: () => number;
  /** Optional global object to publish to (default: `globalThis`).
   *  Tests inject a fake to avoid leaking state between tests. */
  readonly globalTarget?: { __perfStats?: PerfStats };
}

/** Window global hook key. Documented for downstream perf-gate probes. */
export const PERF_STATS_GLOBAL_KEY = '__perfStats' as const;

// ─────────────────────────────────────────────────────────────────────────────
// Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sliding-window FPS: keep a circular buffer of the last 60 timestamps (in ms).
 * FPS = count of entries within the last 1000ms when the buffer is full,
 * OR `frame / (elapsedMs / 1000)` while warming up (fewer than 2 entries or
 * elapsedMs < 1000).
 */
const WINDOW_SIZE = 60;
const WINDOW_DURATION_MS = 1000;

export function createPerfStats(config: PerfStatsConfig): PerfStatsCollector {
  const { renderer } = config;
  const nowFn: () => number = config.now ?? (() => performance.now());
  // Allow tests to inject a fake globalTarget; default to globalThis.
  const target = (config.globalTarget ?? globalThis) as Record<string, PerfStats | undefined>;
  // Postprocessing performs multiple internal renders per game frame. Three's
  // default auto-reset would leave only the final fullscreen pass in the stats.
  const originalAutoReset = renderer.info.autoReset;
  renderer.info.autoReset = false;
  renderer.info.reset();

  // Initialise mutable state
  const startMs = nowFn();
  let frame = 0;
  let elapsedMs = 0;

  // Circular timestamp buffer for sliding-window FPS
  const tsBuf: Float64Array = new Float64Array(WINDOW_SIZE);
  let tsBufHead = 0; // index of the NEXT write position
  let tsBufCount = 0; // how many valid entries are in the buffer (up to WINDOW_SIZE)

  // Cached last snapshot (returned by snapshot() between ticks)
  let current: PerfStats = buildSnapshot(0, 0, 0, 0, 0, 0, 0);

  function buildSnapshot(
    fps: number,
    drawCalls: number,
    triangles: number,
    geometries: number,
    textures: number,
    frameNum: number,
    elapsed: number,
  ): PerfStats {
    return Object.freeze({
      fps,
      drawCalls,
      triangles,
      geometries,
      textures,
      frame: frameNum,
      elapsedMs: elapsed,
    });
  }

  function computeFps(nowMs: number): number {
    if (tsBufCount < 2) {
      // Not enough data: derive from total elapsed
      const secs = elapsedMs / 1000;
      return secs > 0 ? frame / secs : 0;
    }

    // Count how many timestamps fall within the last WINDOW_DURATION_MS
    let count = 0;
    for (let i = 0; i < tsBufCount; i++) {
      const ts = tsBuf[i];
      if (nowMs - ts <= WINDOW_DURATION_MS) {
        count++;
      }
    }
    return count;
  }

  function tick(): PerfStats {
    const nowMs = nowFn();
    elapsedMs = nowMs - startMs;
    frame++;

    // Record this frame's timestamp in the circular buffer
    tsBuf[tsBufHead] = nowMs;
    tsBufHead = (tsBufHead + 1) % WINDOW_SIZE;
    if (tsBufCount < WINDOW_SIZE) {
      tsBufCount++;
    }

    const fps = computeFps(nowMs);

    // Read renderer stats
    const drawCalls = renderer.info.render.calls;
    const triangles = renderer.info.render.triangles;
    const geometries = renderer.info.memory.geometries;
    const textures = renderer.info.memory.textures;

    current = buildSnapshot(fps, drawCalls, triangles, geometries, textures, frame, elapsedMs);
    renderer.info.reset();

    // Publish to global hook
    target[PERF_STATS_GLOBAL_KEY] = current;

    return current;
  }

  function snapshot(): PerfStats {
    return current;
  }

  function reset(): void {
    frame = 0;
    elapsedMs = 0;
    tsBufHead = 0;
    tsBufCount = 0;
    tsBuf.fill(0);
    current = buildSnapshot(0, 0, 0, 0, 0, 0, 0);
    // Do NOT clear the global hook on reset — only dispose() does that.
  }

  function dispose(): void {
    renderer.info.autoReset = originalAutoReset;
    delete target[PERF_STATS_GLOBAL_KEY];
  }

  return { tick, snapshot, reset, dispose };
}
